import React, {useCallback, useEffect, useRef, useState} from "react";
import Styles from './styles.module.scss';
import {useTranslation} from "react-i18next";
import {notificationsApi} from "../../store/cabinet/action";

import bellIcon from '../../theme/assets/redesign/settings/bell.svg';

const POLL_MS = 60 * 1000;

const timeAgo = (iso, t) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = Math.max(0, Date.now() - d.getTime()) / 1000;
    if (diff < 60) return t('только что');
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t('мин назад')}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('ч назад')}`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} ${t('дн назад')}`;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};


// Колокольчик в шапке: счётчик непрочитанных + выпадающий список (GET /notifications)
// size: 'lg' — десктоп 44px, 'sm' — мобилка 40px
const NotificationBell = ({size = 'lg', className = ''}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const rootRef = useRef(null);

    const refreshCount = useCallback(() => {
        notificationsApi.unreadCount().then(setUnread).catch(() => {});
    }, []);

    // счётчик: при загрузке и раз в минуту
    useEffect(() => {
        refreshCount();
        const timer = setInterval(refreshCount, POLL_MS);
        return () => clearInterval(timer);
    }, [refreshCount]);

    const load = useCallback((p = 1) => {
        setLoading(true);
        notificationsApi.list(p)
            .then(r => {
                const pg = r?.notifications || {};
                const data = Array.isArray(pg.data) ? pg.data : [];
                setItems(prev => (p === 1 ? data : [...prev, ...data]));
                setPage(pg.current_page || p);
                setLastPage(pg.last_page || p);
                if (typeof r?.unread_count === 'number') setUnread(r.unread_count);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { if (open) load(1); }, [open, load]);

    // закрытие по клику вне и по Esc
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('touchstart', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('touchstart', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const markRead = (n) => {
        if (n.read_at) return;
        setItems(prev => prev.map(x => (x.id === n.id ? {...x, read_at: new Date().toISOString()} : x)));
        setUnread(u => Math.max(0, u - 1));
        notificationsApi.read(n.id).catch(refreshCount);
    };

    const readAll = () => {
        const now = new Date().toISOString();
        setItems(prev => prev.map(x => ({...x, read_at: x.read_at || now})));
        setUnread(0);
        notificationsApi.readAll().catch(refreshCount);
    };

    const remove = (e, n) => {
        e.stopPropagation();
        setItems(prev => prev.filter(x => x.id !== n.id));
        if (!n.read_at) setUnread(u => Math.max(0, u - 1));
        notificationsApi.remove(n.id).catch(refreshCount);
    };

    return (
        <div className={`${Styles.root} ${className}`} ref={rootRef}>
            <button
                type="button"
                className={`${Styles.bell} ${Styles[size]} ${open ? Styles.bellOpen : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label={t('Уведомления')}
                aria-expanded={open}
            >
                <img src={bellIcon} alt="" width="24" height="24"/>
                {unread > 0 ? <span className={Styles.badge}>{unread > 99 ? '99+' : unread}</span> : null}
            </button>

            {open ? (
                <div className={Styles.panel}>
                    <div className={Styles.head}>
                        <span className={Styles.title}>{t('Уведомления')}</span>
                        {unread > 0 ? (
                            <button type="button" className={Styles.readAll} onClick={readAll}>{t('Прочитать все')}</button>
                        ) : null}
                    </div>

                    <div className={Styles.list}>
                        {items.length ? items.map(n => (
                            <div
                                key={n.id}
                                className={`${Styles.item} ${n.read_at ? '' : Styles.unread}`}
                                onClick={() => markRead(n)}
                                role="button"
                                tabIndex={0}
                            >
                                <span className={`${Styles.dot} ${Styles[`type_${n.type}`] || ''}`}/>
                                <div className={Styles.body}>
                                    <div className={Styles.itemTitle}>{n.data?.title || t('Уведомление')}</div>
                                    {n.data?.text ? <div className={Styles.itemText}>{n.data.text}</div> : null}
                                    <div className={Styles.itemTime}>{timeAgo(n.created_at, t)}</div>
                                </div>
                                <button type="button" className={Styles.remove} onClick={(e) => remove(e, n)} aria-label={t('Удалить')}>
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                    </svg>
                                </button>
                            </div>
                        )) : (
                            <div className={Styles.empty}>{loading ? t('Загрузка...') : t('Уведомлений пока нет')}</div>
                        )}
                        {items.length && page < lastPage ? (
                            <button type="button" className={Styles.more} onClick={() => load(page + 1)} disabled={loading}>
                                {loading ? t('Загрузка...') : t('Показать ещё')}
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default NotificationBell;
